import React, { useState } from 'react';
import { useOrion } from '../context/OrionContext';
import { enviarDocumento } from '../services/helpdeskApi';
import { montarEnvioDocumento } from '../services/documentoPdf';
import { prepararTextoDoDocumento } from '../services/conclusaoDocumento';
import { idsNoTexto } from '../services/textoComAnexos';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// O que aparece DEPOIS que o ticket é enviado.
//
// No 1.4.1 a tela de confirmação continua aberta e, no lugar do botão de envio,
// mostra uma barra de ação que depende do tipo de atendimento:
//
//   Conclusão                        → oferece gerar o documento do atendimento
//   Setor de sistemas / Sup. avançado → oferece registrar o incidente
//
// Mais um lembrete do que não pode ser esquecido — em aviso, não em alert():
// o alert nativo rouba o foco no Windows e trava os campos dos modais seguintes.
//
// A escolha é sempre do operador: nada aqui bloqueia o fechamento.
// ─────────────────────────────────────────────────────────────────────────────

/** Lembrete por tipo de atendimento — o texto é o mesmo do 1.4.1. */
export function lembreteDoTipo(tipo) {
  if (tipo === 'Setor de sistemas') return 'Importante: enviar o TICKET para o gestor responsável.';
  if (tipo === 'Suporte avançado') return 'Recomendado: registre o incidente deste atendimento.';
  if (tipo === 'Comercial') return 'O gestor será avisado automaticamente pelo Teams.';
  return '';
}

export default function PosEnvio({ tipo, numeroTicket, contextoDocumento, aoRegistrarIncidente, aoFechar }) {
  const { avisar } = useOrion();
  const [gerando, setGerando] = useState(false);
  const [passo, setPasso] = useState('');
  const [documentoOk, setDocumentoOk] = useState(false);

  const ofereceIncidente = tipo === 'Setor de sistemas' || tipo === 'Suporte avançado';
  const ofereceDocumento = tipo === 'Conclusão';

  const gerar = async () => {
    if (gerando) return;
    setGerando(true);
    try {
      // ── 1) O texto passa pelo agente antes de virar documento ──
      //
      // O que foi escrito na formalização é para o MySuite: tom interno,
      // jargão de suporte. Este PDF vai para o CLIENTE, então o agente do
      // fluxo `conclusao-form` reescreve em tom didático e tira as tags que
      // sobraram do editor.
      //
      // Nunca lança: se o agente cair, volta o texto original e o documento
      // sai assim mesmo — ver services/conclusaoDocumento.js.
      setPasso('Formalizando o texto…');
      const imagens = contextoDocumento?.imagensPorId || {};
      const { texto, titulo, formalizado, parcial } = await prepararTextoDoDocumento({
        texto: contextoDocumento?.texto || '',
        // As legendas na MESMA ordem em que as imagens aparecem no texto — é a
        // ordem que o operador montou, e é ela que o documento respeita.
        legendas: idsNoTexto(contextoDocumento?.texto || '')
          .map(id => imagens[id]?.legenda || ''),
      });

      // Nada do TICKET vai para o agente: o documento não fala de número,
      // classificação nem solicitante. Ele é o relato da situação, e só.

      if (!formalizado) {
        avisar(parcial
          ? 'Parte do texto saiu como foi escrita: o formalizador devolveu o documento pela metade.'
          : 'O texto saiu como foi escrito: o formalizador do documento não respondeu.',
          { tipo: 'info', duracao: 6000 });
      }

      // ── 2) O PDF é montado AQUI, com as imagens no ponto do texto em que
      // estão, e sobe pronto — como no 1.4.1. Enquanto isso era um POST de
      // JSON, o documento saía sem imagem nenhuma.
      setPasso('Montando o documento…');
      await enviarDocumento(await montarEnvioDocumento({ ...contextoDocumento, texto, titulo }));
      setDocumentoOk(true);
      avisar('Documento gerado e enviado.', { tipo: 'ok' });
    } catch (err) {
      avisar(`Não foi possível gerar o documento: ${err.message}`, { tipo: 'erro' });
    } finally {
      setPasso('');
      setGerando(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={aoFechar}>
      <div className="modal-nx" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            Ticket {numeroTicket} enviado
            <span className="drawer-sub">{tipo}</span>
          </div>
          <button className="drawer-close" onClick={aoFechar}><Icon name="x" size={15} /></button>
        </div>

        <div className="drawer-body">
          {/* Âmbar: é o que ainda falta fazer depois do envio, e é a única
              vez que esse lembrete aparece. No 1.x vinha em alert(). */}
          {lembreteDoTipo(tipo) && (
            <div
              className="panel panel-dest d-atual"
              style={{ marginBottom: ofereceIncidente || ofereceDocumento ? 18 : 0 }}
            >
              <Icon name="alert-triangle" size={14} style={{ marginRight: 6 }} />
              {lembreteDoTipo(tipo)}
            </div>
          )}

          {ofereceIncidente && (
            <div className="panel" style={{ marginBottom: 0 }}>
              <div className="panel-head">
                <div className="panel-title">
                  {tipo === 'Suporte avançado' ? 'Suporte avançado' : 'Setor de Sistemas'}
                  <span className="sub">deseja registrar um incidente?</span>
                </div>
              </div>
              <div className="campo-dica">
                O incidente entra no acervo com o número deste ticket e passa a aparecer
                na busca de erros conhecidos.
              </div>
              <div className="acoes-fim" style={{ margin: '14px 0 0' }}>
                <button className="btn btn-ghost btn-sm" onClick={aoFechar}>Ignorar</button>
                <button className="btn btn-primary btn-sm" onClick={aoRegistrarIncidente}>
                  <Icon name="bug" size={13} /> Registrar incidente
                </button>
              </div>
            </div>
          )}

          {ofereceDocumento && (
            <div className="panel" style={{ marginBottom: 0 }}>
              <div className="panel-head">
                <div className="panel-title">
                  Documento do atendimento
                  <span className="sub">registro formal da conclusão</span>
                </div>
              </div>
              <div className="campo-dica">
                O texto passa pelo formalizador — o documento vai para o cliente, então
                sai em linguagem didática — e as imagens entram na ordem em que você as
                colocou, cada uma com sua legenda.
              </div>
              <div className="acoes-fim" style={{ margin: '14px 0 0' }}>
                <button className="btn btn-ghost btn-sm" onClick={aoFechar}>Agora não</button>
                <button className="btn btn-primary btn-sm" onClick={gerar} disabled={gerando || documentoOk}>
                  {gerando
                    ? <><Icon name="rotate-cw" size={13} className="orion-spin" /> {passo || 'Gerando…'}</>
                    : documentoOk
                      ? <><Icon name="check" size={13} /> Solicitado</>
                      : <><Icon name="file-text" size={13} /> Gerar documento</>}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="drawer-foot">
          <button className="btn btn-primary" onClick={aoFechar}>
            <Icon name="check" size={14} /> Concluir atendimento
          </button>
        </div>
      </div>
    </div>
  );
}
